import { type ComponentFn } from '@littlebird/action-cards-view/react';
import { Children, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { ClampedText } from '../internals/ClampedText';
import { useHostContextPeek } from '../internals/hostContextPeek';
import { formatFullDateTime, formatRelativeTime } from '../internals/localTime';
import { useMinuteTick } from '../internals/useMinuteTick';
import { useTimeFormat } from '../internals/useTimeFormat';
import { sizingClass, TEXT_STYLE, TONE_CLASS } from '../tokens';

import type { catalog } from '@littlebird/action-cards-view';

// The link words in the source line: a dotted underline in the subtle foreground
const SOURCE_LINK_CLASS =
  'rounded-sm text-foreground-subtle underline decoration-border-strongest decoration-dotted underline-offset-[3px] outline-none transition-colors duration-[120ms] hover:text-foreground focus-visible:ring-1 focus-visible:ring-border-strongest motion-reduce:transition-none';

function SourceLink({
  label,
  bound,
  onPress,
}: {
  label: string;
  bound: boolean;
  onPress: () => void;
}) {
  if (!bound) return <span className={SOURCE_LINK_CLASS}>{label}</span>;
  return (
    <button type="button" className={SOURCE_LINK_CLASS} onClick={onPress}>
      {label}
    </button>
  );
}

function SourceLine({ children }: { children: ReactNode }) {
  return (
    <p
      data-testid="reason-source"
      className={cn(
        'flex min-w-0 flex-wrap items-baseline gap-x-[var(--ac-gap-xs)]',
        TEXT_STYLE.meta,
        TONE_CLASS.muted
      )}
    >
      {children}
    </p>
  );
}

// Why the card exists: the kind and where it came from on one wrapping line, the
// agent's reason sentence and what Littlebird saw. Children end the origin
// line: a phone card's urgency and link sit there instead of in its header
export const Reason: ComponentFn<typeof catalog, 'Reason'> = ({
  props,
  children,
  emit,
  on,
}) => {
  useMinuteTick(props.originAt !== undefined);
  const prefer12Hr = useTimeFormat();
  // A host that keeps the reason in its context line draws that line here instead, from the text the document
  // resolved, such as the message a reply answers when the card has no reason
  const contextPeek = useHostContextPeek();
  if (contextPeek) return contextPeek.line(props.text || null);
  // A hidden child renders nothing, so its wrapper and dot go with it
  const endings = Children.toArray(children).map((ending, index) => (
    <span
      key={index}
      className="max-w-full truncate empty:hidden before:mr-[var(--ac-gap-xs)] before:content-['·']"
    >
      {ending}
    </span>
  ));
  const { text } = props;
  const clamp = props.clamp ?? 2;

  const sourcePieces: { key: string; node: ReactNode }[] = [];
  if (props.kind) {
    sourcePieces.push({
      key: 'kind',
      node: (
        <span
          className={cn('font-semibold', TONE_CLASS[props.kindTone ?? 'muted'])}
        >
          {props.kind}
        </span>
      ),
    });
  }
  if (props.originKind === 'chat' && props.originPrefix)
    sourcePieces.push({ key: 'prefix', node: props.originPrefix });
  if (props.originPrefix || props.originLabel) {
    sourcePieces.push({
      key: 'origin',
      node: (
        <span>
          {props.originKind !== 'chat' ? props.originPrefix : null}
          {props.originKind !== 'chat' &&
          props.originPrefix &&
          props.originLabel
            ? ' '
            : null}
          {props.originLabel ? (
            <SourceLink
              label={props.originLabel}
              bound={on('press').bound}
              onPress={() => emit('press')}
            />
          ) : null}
        </span>
      ),
    });
  }
  if (props.originDetail) {
    sourcePieces.push({ key: 'detail', node: props.originDetail });
  }
  if (props.originAt) {
    sourcePieces.push({
      key: 'at',
      node: (
        <time
          dateTime={props.originAt}
          title={formatFullDateTime(props.originAt, prefer12Hr)}
        >
          {formatRelativeTime(props.originAt)}
        </time>
      ),
    });
  }
  if (props.project) {
    sourcePieces.push({
      key: 'project',
      node: (
        <SourceLink
          label={props.project}
          bound={on('pressProject').bound}
          onPress={() => emit('pressProject')}
        />
      ),
    });
  }

  const empty =
    sourcePieces.length === 0 &&
    endings.length === 0 &&
    !text &&
    !props.evidence;
  if (empty) return null;

  return (
    <div
      data-card-reason
      className={cn(
        'flex min-w-0 flex-col gap-[var(--ac-gap-xs)]',
        sizingClass(props.sizing)
      )}
    >
      {sourcePieces.length > 0 || endings.length > 0 ? (
        <SourceLine>
          {sourcePieces.map((piece, index) => (
            <span key={piece.key} className="max-w-full truncate">
              {index > 0 ? <span aria-hidden="true"> · </span> : null}
              {piece.node}
            </span>
          ))}
          {endings}
        </SourceLine>
      ) : null}
      <div className="flex min-w-0 flex-col gap-[var(--ac-gap-xs)]">
        {text ? (
          <div
            className={cn(
              'flex min-w-0 gap-[calc(6*var(--ac-u))] text-[length:var(--ac-reason)] leading-[var(--ac-reason-lh)]'
            )}
          >
            {props.tone === 'danger' ? (
              <span
                aria-hidden="true"
                data-testid="reason-danger-mark"
                className="mt-[calc(0.5lh-3*var(--ac-u))] size-[calc(6*var(--ac-u))] shrink-0 rounded-full bg-danger-9"
              />
            ) : null}
            <ClampedText
              lines={clamp > 0 ? clamp : undefined}
              disclosure="reason"
              className={cn('min-w-0', TONE_CLASS.subtle)}
            >
              {text}
            </ClampedText>
          </div>
        ) : null}
        {props.evidence ? (
          <p className={cn(TEXT_STYLE.meta, TONE_CLASS.muted)}>
            {props.evidence}
          </p>
        ) : null}
      </div>
    </div>
  );
};
